import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Building2, Edit, Archive } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const emptyForm = { name: '', email: '', phone: '', notes: '' };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [clientToArchive, setClientToArchive] = useState(null);
  const [filter, setFilter] = useState('active');
  
  // Separate state for create and edit forms to prevent cross-contamination
  const [createFormData, setCreateFormData] = useState(emptyForm);
  const [editFormData, setEditFormData] = useState(emptyForm);

  const fetchClients = useCallback(async (currentFilter) => {
    try {
      let url = `${API_URL}/api/clients`;
      if (currentFilter === 'active') {
        url += '?is_active=true';
      } else if (currentFilter === 'archived') {
        url += '?is_active=false';
      }
      const response = await axios.get(url);
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients(filter);
  }, [filter, fetchClients]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/clients`, createFormData);
      toast.success('Client created successfully');
      setDialogOpen(false);
      setCreateFormData(emptyForm);
      fetchClients(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setEditFormData({
      name: client.name,
      email: client.email || '',
      phone: client.phone,
      notes: client.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API_URL}/api/clients/${selectedClient.id}`, editFormData);
      toast.success('Client updated successfully');
      setEditDialogOpen(false);
      setSelectedClient(null);
      setEditFormData(emptyForm);
      fetchClients(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    }
  };

  const handleArchive = async (clientId) => {
    try {
      await axios.patch(`${API_URL}/api/clients/${clientId}/archive`);
      toast.success('Client archived');
      fetchClients(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive client');
    } finally {
      setArchiveDialogOpen(false);
      setClientToArchive(null);
    }
  };

  const openArchiveDialog = (client) => {
    setClientToArchive(client);
    setArchiveDialogOpen(true);
  };

  const handleRestore = async (clientId) => {
    try {
      await axios.patch(`${API_URL}/api/clients/${clientId}/restore`);
      toast.success('Client restored');
      fetchClients(filter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore client');
    }
  };

  // Stable handlers using functional updates
  const handleCreateNameChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({ ...prev, name: value }));
  }, []);

  const handleCreateEmailChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({ ...prev, email: value }));
  }, []);

  const handleCreatePhoneChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({ ...prev, phone: value }));
  }, []);

  const handleCreateNotesChange = useCallback((e) => {
    const value = e.target.value;
    setCreateFormData(prev => ({ ...prev, notes: value }));
  }, []);

  const handleEditNameChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({ ...prev, name: value }));
  }, []);

  const handleEditEmailChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({ ...prev, email: value }));
  }, []);

  const handleEditPhoneChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({ ...prev, phone: value }));
  }, []);

  const handleEditNotesChange = useCallback((e) => {
    const value = e.target.value;
    setEditFormData(prev => ({ ...prev, notes: value }));
  }, []);

  const handleCreateDialogChange = useCallback((open) => {
    setDialogOpen(open);
    if (!open) {
      setCreateFormData(emptyForm);
    }
  }, []);

  const handleEditDialogChange = useCallback((open) => {
    setEditDialogOpen(open);
    if (!open) {
      setSelectedClient(null);
      setEditFormData(emptyForm);
    }
  }, []);

  return (
    <Layout>
      <div className="space-y-6" data-testid="clients-page">
        <div className="flex justify-end items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="client-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={dialogOpen} onOpenChange={handleCreateDialogChange}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600" data-testid="add-client-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Client</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="client-form">
                  <div>
                    <Label htmlFor="name">Client Name</Label>
                    <Input
                      id="name"
                      value={createFormData.name}
                      onChange={handleCreateNameChange}
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={createFormData.email}
                      onChange={handleCreateEmailChange}
                      data-testid="client-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={createFormData.phone}
                      onChange={handleCreatePhoneChange}
                      required
                      data-testid="client-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={createFormData.notes}
                      onChange={handleCreateNotesChange}
                      rows={3}
                      data-testid="client-notes-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="submit-client-button">
                    Create Client
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
        </div>

        {loading ? (
          <div>Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map((client) => (
              <div
                key={client.id}
                className={`bg-white border p-6 hover:border-slate-300 transition-colors ${
                  client.is_active ? 'border-slate-200' : 'border-slate-300 bg-slate-50'
                }`}
                data-testid={`client-card-${client.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 bg-orange-100 border border-orange-200 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">{client.name}</h3>
                      {!client.is_active && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs font-bold bg-slate-200 text-slate-700 border border-slate-300">
                          ARCHIVED
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-slate-600 mb-4">
                  <p>{client.phone}</p>
                  {client.email && <p>{client.email}</p>}
                  {client.notes && <p className="text-xs mt-2 text-slate-500">{client.notes}</p>}
                </div>
                <div className="flex gap-2 pt-3 border-t border-slate-200">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(client)}
                    className="flex-1"
                    data-testid={`edit-client-${client.id}`}
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  {client.is_active ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openArchiveDialog(client)}
                      className="border-slate-200 text-slate-600 hover:bg-slate-50"
                      data-testid={`archive-client-${client.id}`}
                    >
                      <Archive className="w-3 h-3 mr-1" />
                      Archive
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestore(client.id)}
                      className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                      data-testid={`restore-client-${client.id}`}
                    >
                      Restore
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={editDialogOpen} onOpenChange={handleEditDialogChange}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4" data-testid="edit-client-form">
              <div>
                <Label htmlFor="edit_name">Client Name</Label>
                <Input
                  id="edit_name"
                  value={editFormData.name}
                  onChange={handleEditNameChange}
                  required
                  data-testid="edit-client-name-input"
                />
              </div>
              <div>
                <Label htmlFor="edit_email">Email</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={editFormData.email}
                  onChange={handleEditEmailChange}
                  data-testid="edit-client-email-input"
                />
              </div>
              <div>
                <Label htmlFor="edit_phone">Phone</Label>
                <Input
                  id="edit_phone"
                  value={editFormData.phone}
                  onChange={handleEditPhoneChange}
                  required
                  data-testid="edit-client-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="edit_notes">Notes</Label>
                <Textarea
                  id="edit_notes"
                  value={editFormData.notes}
                  onChange={handleEditNotesChange}
                  rows={3}
                  data-testid="edit-client-notes-input"
                />
              </div>
              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="update-client-button">
                Update Client
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Archive Client</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to archive "{clientToArchive?.name}"? No new visits can be created for archived clients.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => handleArchive(clientToArchive?.id)}
                className="bg-orange-500 hover:bg-orange-600"
              >
                Archive
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
